/**
 * Poppedijn Predictive Search
 * ──────────────────────────────────────────────
 * Uses Shopify's Predictive Search API for live results.
 * Features:
 * - Debounced input (250ms)
 * - Shows products with images, prices, vendor
 * - Shows collections and pages
 * - Dutch synonym expansion for better recall
 * - Keyboard navigation (arrow keys + enter)
 * - Recent searches memory
 * - Beautiful inline results
 */

(function () {
  'use strict';

  var DEBOUNCE_MS = 250;
  var MIN_CHARS = 2;
  var MAX_PRODUCTS = 6;
  var MAX_COLLECTIONS = 3;
  var MAX_PAGES = 2;
  var RECENT_KEY = 'poppedijn_recent_searches';
  var MAX_RECENT = 5;

  /* ── Dutch synonyms for better search recall ── */
  var SYNONYMS = {
    'speelgoed': ['toys', 'spellen', 'spel'],
    'houten': ['hout', 'wooden'],
    'baby': ['babys', "baby's", 'peuter', 'dreumes', 'newborn'],
    'peuter': ['kleuter', 'baby', 'dreumes'],
    'kleuter': ['peuter', 'kind'],
    'puzzel': ['puzzels', 'puzzle', 'legpuzzel'],
    'blokken': ['blok', 'bouwblokken', 'stapelblokken', 'bouwen'],
    'bouwen': ['blokken', 'constructie', 'bouwset'],
    'pop': ['poppen', 'poppenhuis', 'knuffel'],
    'knuffel': ['knuffels', 'knuffeldier', 'pluche', 'pop'],
    'auto': ['autos', "auto's", 'voertuig', 'trein', 'wagen'],
    'trein': ['treinen', 'treinbaan', 'spoor'],
    'kleding': ['kleren', 'kledij', 'outfit', 'shirt', 'broek'],
    'cadeau': ['kado', 'geschenk', 'cadeautje', 'gift'],
    'kado': ['cadeau', 'geschenk'],
    'verjaardag': ['jarig', 'verjaardagscadeau'],
    'duurzaam': ['eco', 'biologisch', 'groen', 'verantwoord'],
    'creatief': ['knutselen', 'tekenen', 'schilderen', 'knutsel'],
    'knutselen': ['creatief', 'knutsel', 'knutselset'],
    'muziek': ['muziekinstrument', 'instrument', 'xylofoon', 'trommel'],
    'regenboog': ['rainbow', 'regenboogblokken'],
    'grimms': ["grimm's", 'grimm'],
    "grimm's": ['grimms', 'grimm'],
    'stapeltoren': ['stapelen', 'toren', 'stapelblokken'],
    'bijtspeelgoed': ['bijtring', 'bijten', 'tandjes'],
    'rammelaar': ['rammel', 'rammelen', 'babyspeelgoed'],
  };

  /* ── Recent searches ── */
  function getRecentSearches() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; }
    catch (e) { return []; }
  }

  function addRecentSearch(term) {
    var recent = getRecentSearches().filter(function (s) { return s !== term; });
    recent.unshift(term);
    if (recent.length > MAX_RECENT) recent = recent.slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  }

  /* ── Synonym expansion ── */
  function expandQuery(query) {
    var words = query.toLowerCase().trim().split(/\s+/);
    var expanded = [query];

    words.forEach(function (word) {
      if (SYNONYMS[word]) {
        SYNONYMS[word].forEach(function (syn) {
          var alt = query.toLowerCase().replace(word, syn);
          if (expanded.indexOf(alt) === -1) expanded.push(alt);
        });
      }
    });

    return expanded;
  }

  /* ── Debounce ── */
  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* ── Format price ── */
  function formatPrice(priceStr) {
    if (!priceStr && priceStr !== 0 && priceStr !== '0') return '';
    /* Shopify Predictive Search API returns prices as '44.95' (euros, not cents) */
    var num = parseFloat(String(priceStr));
    if (isNaN(num)) return '';
    var euros = num.toFixed(2).replace('.', ',');
    return '€' + euros;
  }

  /* ── Predictive Search API ── */
  function fetchPredictiveResults(query) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
      '&resources[type]=product,collection,page' +
      '&resources[limit]=' + MAX_PRODUCTS +
      '&resources[options][unavailable_products]=hide' +
      '&resources[options][fields]=title,product_type,vendor,tag,body';

    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        return data.resources ? data.resources.results : null;
      });
  }

  /* ── Render results ── */
  function renderResults(container, results, query) {
    if (!results) {
      container.innerHTML = '<div class="ps-empty">Er ging iets mis. Probeer het opnieuw.</div>';
      return;
    }

    var products = results.products || [];
    var collections = results.collections || [];
    var pages = results.pages || [];
    var hasResults = products.length > 0 || collections.length > 0 || pages.length > 0;

    if (!hasResults) {
      container.innerHTML =
        '<div class="ps-empty">' +
          '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-border)" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
          '<p>Geen resultaten voor <strong>"' + escapeHtml(query) + '"</strong></p>' +
          '<span>Probeer een andere zoekterm, zoals een merknaam of leeftijd</span>' +
        '</div>';
      return;
    }

    var html = '';

    /* Products section */
    if (products.length > 0) {
      html += '<div class="ps-section">';
      html += '<h4 class="ps-section__title">Producten</h4>';
      html += '<div class="ps-products">';
      products.forEach(function (p) {
        var img = p.image ? p.featured_image || p.image : null;
        var imgUrl = img ? (typeof img === 'string' ? img : img.url) : '';
        if (imgUrl && imgUrl.indexOf('?') === -1) imgUrl += '?width=120';
        else if (imgUrl) imgUrl += '&width=120';

        var price = p.price ? formatPrice(p.price) : '';
        var comparePrice = p.compare_at_price_max && parseFloat(p.compare_at_price_max) > parseFloat(p.price)
          ? formatPrice(p.compare_at_price_max) : '';

        html += '<a href="' + escapeHtml(p.url) + '" class="ps-product" data-ps-item>';
        html += '<div class="ps-product__image">';
        if (imgUrl) {
          html += '<img src="' + escapeHtml(imgUrl) + '" alt="' + escapeHtml(p.title) + '" width="60" height="60" loading="lazy">';
        } else {
          html += '<div class="ps-product__placeholder"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-border)" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>';
        }
        html += '</div>';
        html += '<div class="ps-product__info">';
        if (p.vendor) html += '<span class="ps-product__vendor">' + escapeHtml(p.vendor) + '</span>';
        html += '<span class="ps-product__title">' + highlightMatch(p.title, query) + '</span>';
        html += '<span class="ps-product__price">';
        if (comparePrice) html += '<s class="ps-product__compare">' + comparePrice + '</s> ';
        html += price;
        html += '</span>';
        html += '</div>';
        html += '</a>';
      });
      html += '</div>';
      html += '</div>';
    }

    /* Collections section */
    if (collections.length > 0) {
      html += '<div class="ps-section">';
      html += '<h4 class="ps-section__title">Collecties</h4>';
      collections.slice(0, MAX_COLLECTIONS).forEach(function (c) {
        html += '<a href="' + escapeHtml(c.url) + '" class="ps-collection" data-ps-item>';
        html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>';
        html += '<span>' + highlightMatch(c.title, query) + '</span>';
        html += '</a>';
      });
      html += '</div>';
    }

    /* Pages section */
    if (pages.length > 0) {
      html += '<div class="ps-section">';
      html += '<h4 class="ps-section__title">Pagina\'s</h4>';
      pages.slice(0, MAX_PAGES).forEach(function (pg) {
        html += '<a href="' + escapeHtml(pg.url) + '" class="ps-collection" data-ps-item>';
        html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>';
        html += '<span>' + highlightMatch(pg.title, query) + '</span>';
        html += '</a>';
      });
      html += '</div>';
    }

    /* View all link */
    html += '<a href="/search?q=' + encodeURIComponent(query) + '" class="ps-view-all" data-ps-item>';
    html += 'Alle resultaten bekijken voor "' + escapeHtml(query) + '"';
    html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>';
    html += '</a>';

    container.innerHTML = html;
  }

  /* ── Render recent searches ── */
  function renderRecent(container) {
    var recent = getRecentSearches();
    if (recent.length === 0) {
      container.innerHTML = '';
      return;
    }

    var html = '<div class="ps-section">';
    html += '<h4 class="ps-section__title">Eerder gezocht</h4>';
    recent.forEach(function (term) {
      html += '<a href="/search?q=' + encodeURIComponent(term) + '" class="ps-recent" data-ps-recent="' + escapeHtml(term) + '" data-ps-item>';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
      html += '<span>' + escapeHtml(term) + '</span>';
      html += '</a>';
    });
    html += '</div>';
    container.innerHTML = html;
  }

  /* ── Helpers ── */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Highlight op de RUWE tekst, daarna per segment escapen.
     Voorkomt dat zoekwoorden binnen HTML-entities matchen en entities breken. */
  function highlightMatch(text, query) {
    if (!text) return '';
    if (!query) return escapeHtml(text);
    var words = query.trim().split(/\s+/).filter(function (w) { return w.length >= 2; });
    if (!words.length) return escapeHtml(text);
    /* Langste woorden eerst, anders wint een korter overlappend woord de match */
    var pattern = words.slice().sort(function (a, b) { return b.length - a.length; }).map(function (w) {
      return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }).join('|');
    var regex = new RegExp(pattern, 'gi');
    var out = '';
    var lastIndex = 0;
    var m;
    while ((m = regex.exec(text)) !== null) {
      out += escapeHtml(text.slice(lastIndex, m.index));
      out += '<mark>' + escapeHtml(m[0]) + '</mark>';
      lastIndex = m.index + m[0].length;
      if (m.index === regex.lastIndex) regex.lastIndex++;
    }
    out += escapeHtml(text.slice(lastIndex));
    return out;
  }

  /* ── Keyboard navigation ── */
  function setupKeyboardNav(container, input) {
    var activeIndex = -1;

    input.addEventListener('keydown', function (e) {
      var items = container.querySelectorAll('[data-ps-item]');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        updateActiveItem(items, activeIndex);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, -1);
        if (activeIndex === -1) {
          items.forEach(function (i) { i.classList.remove('ps-active'); });
          input.focus();
        } else {
          updateActiveItem(items, activeIndex);
        }
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        items[activeIndex].click();
      } else if (e.key === 'Escape') {
        var overlay = document.querySelector('[data-search-overlay]');
        if (overlay) overlay.classList.remove('is-open');
      }
    });

    /* Reset index when results change */
    var observer = new MutationObserver(function () { activeIndex = -1; });
    observer.observe(container, { childList: true });
  }

  function updateActiveItem(items, index) {
    items.forEach(function (i, idx) {
      i.classList.toggle('ps-active', idx === index);
    });
    if (items[index]) items[index].scrollIntoView({ block: 'nearest' });
  }

  /* ── Init ── */
  function init() {
    var overlay = document.querySelector('[data-search-overlay]');
    var input = document.querySelector('[data-search-input]');
    if (!overlay || !input) return;

    /* Create results container */
    var resultsContainer = document.createElement('div');
    resultsContainer.className = 'ps-results';
    resultsContainer.setAttribute('data-ps-results', '');

    /* Onzichtbare statusregel die screenreaders het aantal resultaten meldt */
    var statusRegion = document.createElement('div');
    statusRegion.className = 'visually-hidden';
    statusRegion.setAttribute('role', 'status');
    statusRegion.setAttribute('aria-live', 'polite');

    /* Insert after the form */
    var form = overlay.querySelector('.search-overlay__form');
    if (form) {
      form.appendChild(resultsContainer);
      form.appendChild(statusRegion);
    }

    function announceResults() {
      var count = resultsContainer.querySelectorAll('[data-ps-item]').length;
      statusRegion.textContent = count > 0
        ? count + ' resultaten gevonden, gebruik pijltjestoetsen om te bladeren'
        : 'Geen resultaten gevonden';
    }

    /* Setup keyboard nav */
    setupKeyboardNav(resultsContainer, input);

    /* Prevent form submission when results are open - navigate to search page */
    form.addEventListener('submit', function (e) {
      var q = input.value.trim();
      if (q.length >= MIN_CHARS) {
        addRecentSearch(q);
      }
    });

    /* Handle input */
    var doSearch = debounce(function () {
      var query = input.value.trim();

      if (query.length < MIN_CHARS) {
        renderRecent(resultsContainer);
        return;
      }

      /* Show loading state */
      resultsContainer.innerHTML = '<div class="ps-loading"><div class="ps-loading__spinner"></div><span>Zoeken...</span></div>';

      /* Try primary query first, then synonyms if no results */
      var queries = expandQuery(query);
      var primaryQuery = queries[0];

      fetchPredictiveResults(primaryQuery).then(function (results) {
        var products = results && results.products ? results.products : [];

        if (products.length === 0 && queries.length > 1) {
          /* Try synonym-expanded queries */
          var promises = queries.slice(1, 3).map(function (q) {
            return fetchPredictiveResults(q);
          });

          Promise.all(promises).then(function (allResults) {
            /* Merge results, deduplicate by URL */
            var merged = { products: [], collections: [], pages: [] };
            var seen = {};

            [results].concat(allResults).forEach(function (r) {
              if (!r) return;
              ['products', 'collections', 'pages'].forEach(function (type) {
                (r[type] || []).forEach(function (item) {
                  if (!seen[item.url]) {
                    seen[item.url] = true;
                    merged[type].push(item);
                  }
                });
              });
            });

            renderResults(resultsContainer, merged, query);
            announceResults();
          });
        } else {
          renderResults(resultsContainer, results, query);
          announceResults();
        }
      }).catch(function () {
        resultsContainer.innerHTML = '<div class="ps-empty">Zoeken mislukt. Controleer je internetverbinding.</div>';
      });

    }, DEBOUNCE_MS);

    input.addEventListener('input', doSearch);

    /* Show recent searches on focus when empty */
    input.addEventListener('focus', function () {
      if (input.value.trim().length < MIN_CHARS) {
        renderRecent(resultsContainer);
      }
    });

    /* Handle clicking recent search terms */
    resultsContainer.addEventListener('click', function (e) {
      var recent = e.target.closest('[data-ps-recent]');
      if (recent) {
        e.preventDefault();
        var term = recent.dataset.psRecent;
        input.value = term;
        input.dispatchEvent(new Event('input'));
      }
    });
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
